import {usePostApi} from 'common/hooks/usePostApi';
import React, {useCallback, useEffect} from 'react';
import qs from 'query-string';
import {useTranslation} from 'react-i18next';
import DraggableModal from 'components/DraggableModal';
import {IJobSequence} from 'types/diplomat/job/JobSequence';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {packId, unpackId} from 'common/diplomatIdUtil';
import {castToSend} from 'common/yup/castToSend';
import {useSelector} from 'react-redux';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {transactionSynchronizationGetters} from 'redux/transactions/transactionSynchronizationGetters';
import {IDiplomatObject} from 'types/DiplomatObject';
import {DialogTypes} from 'common/names';
import {openDialogOutside} from 'common/openDialogOutside';
import {RunNowPanel} from 'features/layout/CommonPage/panels/TransactionPanel/SchedulePanel/RunNow/RunNowPanel';
import {pageSelectors} from 'features/layout/CommonPage/redux';
import {ITransaction} from 'types/diplomat/transaction/Transaction';
import DialogActions from '@mui/material/DialogActions';
import Button from 'components/Button';

const openOutside = (
   id: string,
   isSynchronization: boolean,
   runNowError: string,
   jobSequence: string
) => () =>
   openDialogOutside(DialogTypes.RunNowTransactionDialog.id, {
      id,
      isSynchronization,
      runNowError,
      jobSequence,
   });

type Props = {
   data: Pick<INav, '_packedId' | 'id' | 'name'> & {dryRun: boolean}; // objectType is inside id;
   onClose: () => void;
};

const useId = (data: Props['data']) => {
   // const params = useParams<{id: string; objectType: string}>();
   const {id, objectType} = unpackId(data._packedId || data.id);

   return {
      id,
      objectType,
      packedId: data?._packedId || data?.id || packId(objectType, id),
   };
};

export const RunNowDialog = React.memo(({data, onClose}: Props) => {
   const {t} = useTranslation();

   const {id: transactionOrSyncId, objectType, packedId} = useId(data);

   const transaction = useSelector(pageSelectors.getValues(packedId)) as ITransaction;

   const isSynchronization = objectType.toUpperCase() === DiplomatObjectType.enum.SYNCHRONIZATION;

   const {apiRuNow, runNowIdName, schema, label} = isSynchronization
      ? transactionSynchronizationGetters.Synchronization
      : transactionSynchronizationGetters.Transaction;

   const doPreProcessing = transaction?.schedule.doPreProcessing;
   const transactionName = data?.name || transaction?.name;

   const {request: runNow, state: jobSequence, error: runNowError} = usePostApi<IJobSequence>(
      qs.stringifyUrl({
         url: apiRuNow,
         query: {
            [runNowIdName]: transaction ? undefined : transactionOrSyncId,
            doPreProcessing,
            doDryRun: data?.dryRun ? 'true' : undefined,
         },
      })
   );

   const run = useCallback(() => {
      let payload = null;
      if (transaction) {
         const t = castToSend<IDiplomatObject>(schema, transaction, {
            stripUnknown: true,
         });
         payload = JSON.stringify(t);
      }

      runNow(payload);
   }, [runNow, transaction, schema]);

   useEffect(() => {
      if (data?.dryRun) {
         run();
      }
   }, []);

   const handleClose = onClose;

   return (
      <DraggableModal
         width={'md'}
         // open={true}
         onClose={handleClose}
         header={t('Run {{label}} {{transactionName}}', {label, transactionName})}
         onLaunch={
            jobSequence
               ? openOutside(
                    transactionOrSyncId,
                    isSynchronization,
                    runNowError,
                    jobSequence?.result!
                 )
               : undefined
         }
      >
         {jobSequence || data?.dryRun ? (
            <RunNowPanel
               id={transactionOrSyncId}
               isSynchronization={isSynchronization}
               runNowError={runNowError}
               jobSequence={jobSequence?.result!}
               onClose={handleClose}
            />
         ) : (
            <>
               {t('Run {{label}} {{transactionName}}?', {label, transactionName})}
               <DialogActions>
                  <Button
                     onClick={() => {
                        run();
                     }}
                     color="primary"
                  >
                     {t('Run')}
                  </Button>
                  <Button autoFocus onClick={handleClose} color="primary">
                     {t('Close')}
                  </Button>
               </DialogActions>
            </>
         )}
      </DraggableModal>
   );
});
